// 外部库
import React, {
  ForwardedRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useCallback,
  useState
} from 'react';
import classNames from 'classnames';
import { cloneDeep } from 'lodash-es';
// 内部工具和配置
import { DATALAB_SPLIT_STR } from '@dbl/core/global';
import { useDblIntl } from '@/pages/datalab/utils/utils';
// 类型和接口
import { ChartRef, LineChartProps } from '../_config/_interface';
// 内部 hooks 和工具函数
import { useEventData } from '../_config/_hooks';
import { formatDisplayValue } from '@customComp/Chart/_config/_utils';
import useReferenceLine from '@customComp/Chart/chartConfigs/useReferenceLine';
import { adjustChartMargin, setDataLabel, setXLabelAlign } from '@customComp/Chart/chartConfigs/plugs';
import { setTooltipMaxSize } from '@customComp/Chart/chartConfigs/tooltip/config';
import { useLineOption } from '@customComp/Chart/chartConfigs/lineChartHooks';
import { changeLegendLocation } from '@customComp/Chart/chartConfigs/legend/legendUtils';
import {
  setYaxisRange,
  setDualYAxis,
} from '@customComp/Chart/chartConfigs/yAxis/utils';
// 组件
import Chart from '..';
import TextBubbles from '@home/components/TextBubbles';

const LineChartInner: React.ForwardRefRenderFunction<ChartRef, LineChartProps> = (props, ref: ForwardedRef<ChartRef>) => {
  const {
    isEvent,
    eventData,
    chartData,
    metricNames,
    isOnlyOneDimension,
    tooltipMinTopPx,
    metricIsAllPercent,
    chartConfig,
    compareDateCount,
    fieldInfos,
    userFilterControls,
    setOtherConfigFunc,
    onEvents: externalOnEvents,
  } = props;
  const { formatMessage, isEn } = useDblIntl('chart');
  const chartRef = useRef<ChartRef>(null);
  // 获取实例
  const [ecInstance, setEcInstance] = useState(chartRef?.current?.instance());
  // 图表布局尺寸，随 rendered/finished 更新，供后续布局或透传使用
  const [chartLayoutRect, setChartLayoutRect] = useState<{
    width: number;
    height: number;
    gridWidth: number;
    gridHeight: number;
  }>({ width: 0, height: 0, gridWidth: 0, gridHeight: 0 });
  const instance = useCallback(
    () => chartRef?.current?.instance(),
    [chartRef],
  );
  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    ecInstance,
    instance,
  }));
  const chart = chartRef?.current?.cRef;

  const displayConfig = useMemo(() => {
    const config: Record<string, { accuracy: number; unit: string; }> = {};
    metricNames.forEach((metricName: string) => {
      const { accuracy, unit } = fieldInfos?.find((fieldInfo: { name: string; }) => fieldInfo.name === metricName) || { accuracy: 0, unit: 'bmk' };
      config[metricName] = { accuracy, unit };
    });
    return config;
  }, [fieldInfos, metricNames]);

  const option = useLineOption({
    xAxisData: chartData?.xAxisData || [],
    seriesData: chartData?.seriesData || [],
    compareDateCount,
    metricIsAllPercent,
    metricNames,
    tooltipMinTopPx,
    isOnlyOneDimension,
    fieldInfos,
    chartConfig,
    userFilterControls,
    setOtherConfigFunc: (opt) => {
      let option = cloneDeep(opt);
      // 设置数据标签
      option = setDataLabel(
        option,
        chartConfig,
        {
          metricFormatter: (param, isPercent) => {
            const { accuracy, unit } = displayConfig[param?.data?.metric] || { accuracy: 0, unit: 'bmk' };
            return formatDisplayValue({
              value: param?.data?.rawValue,
              isPercent,
              accuracy,
              unit,
            });
          },
          dimensionFormatter: (params) => {
            if (isOnlyOneDimension) return '';
            const { seriesName = '', data = {} } = params || {};
            if (data?.isCompare) {
              return seriesName.split(DATALAB_SPLIT_STR)[0];
            }
            return seriesName;
          }
        }
      );
      // 设置tooltip最大尺寸
      option = setTooltipMaxSize(option, chartConfig, { chartLayoutRect });
      // 设置图表边距
      adjustChartMargin(option, chartConfig, {
        ecInstance: instance?.(),
        topAuto: chartConfig?.showDataLabel,
        rightAuto: chartConfig?.showDataLabel,
      });
      // 设置图例位置
      if (chartConfig?.legendPosition) {
        changeLegendLocation({
          option,
          position: chartConfig.legendPosition,
        });
      }
      // 双y轴设置
      if (chartConfig?.dualYAxisEnabled) {
        option = setDualYAxis(option, chartConfig, { fieldInfos });
      }
      // y轴range调整
      const primaryYAxisRangeSwitch = chartConfig?.dualYAxisEnabled
        ? !!chartConfig?.primaryYAxis?.yAxisRangeSwitch
        : !!chartConfig?.yAxisRangeSwitch;
      const secondaryYAxisRangeSwitch = !!chartConfig?.secondYAxis?.yAxisRangeSwitch;
      if (primaryYAxisRangeSwitch && secondaryYAxisRangeSwitch) {
        option = setYaxisRange({ option });
      } else if (primaryYAxisRangeSwitch) {
        option = setYaxisRange({ option, index: '0' });
      } else if (secondaryYAxisRangeSwitch) {
        option = setYaxisRange({ option, index: '1' });
      }
      return setOtherConfigFunc ? setOtherConfigFunc?.(option) : option;
    }
  });

  let { lineOption: newOption } = useEventData({
    chart,
    ecInstance,
    chartLayoutRect,
    metricNames,
    option,
    isEvent,
    eventData,
    formatMessage,
    isEn,
    isOnlyOneDimension,
    compareDateCount,
    fieldInfos,
    chartConfig,
  });

  const handleChartRendered = useCallback((): void => {
    const chartInstance = instance?.() as any;
    setEcInstance((prev) => {
      if (prev === chartInstance) {
        return prev;
      }
      return chartInstance;
    });
    // 获取实例尺寸
    const instanceRect = chartInstance?.getDom()?.getBoundingClientRect?.();
    const width = instanceRect?.width ?? 400;
    const height = instanceRect?.height ?? 379;
    // 获取绘图区尺寸
    const gridRect = chartInstance?.getModel?.()?.getComponent?.('grid', 0)?.coordinateSystem?.getRect?.();
    const gridWidth = gridRect?.width ?? 400;
    const gridHeight = gridRect?.height ?? 379;
    setChartLayoutRect((prev) => {
      // 图表宽高不变时保持 state 引用不变，避免重渲染 → Chart setOption → rendered 死循环
      // 这里只比较图表宽高，因为绘图区宽高可能随着数据变化而变化
      // 增加绘图区高度判断，避免x轴倾斜时，事件折线图展示名称被遮挡
      if (prev.width === width && prev.height === height && prev.gridHeight === gridHeight) {
        return prev;
      }
      return { width, height, gridWidth, gridHeight };
    });
  }, [instance]);

  // 监听图表渲染事件，拿到最新绘图区高度
  const onEvents = useMemo(() => {
    const events: Record<string, (event: any, chart: any) => void> = {
      rendered: handleChartRendered,
      finished: handleChartRendered,
      ...(externalOnEvents ?? {}),
    };
    return events;
  }, [handleChartRendered, externalOnEvents]);

  newOption = useReferenceLine({ option: newOption, referenceLines: chartConfig?.referenceLine, autoYRange: chartConfig?.yAxisRangeSwitch, isPercent: metricIsAllPercent });
  newOption = useMemo(() => setXLabelAlign(newOption, { type: chartConfig?.xLabel }), [chartConfig?.xLabel, newOption]);

  // 获取y轴名称
  const {
    leftYAxisName,
    rightYAxisName,
    hasYAxisName,
  } = useMemo(() => {
    const yAxisList = Array.isArray(newOption?.yAxis) ? newOption?.yAxis : [];
    const legacyYAxisName = chartConfig?.yAxisName || '';
    const leftYAxisName = yAxisList?.[0]?.name || legacyYAxisName;
    const rightYAxisName = yAxisList?.[1]?.name || '';
    const hasYAxisName = Boolean(leftYAxisName) || Boolean(rightYAxisName);
    return {
      leftYAxisName,
      rightYAxisName,
      hasYAxisName,
    };
  }, [newOption, chartConfig?.yAxisName]);

  return <div className="analysis-chart-container">
    <div
      className={classNames('dbl-charts-top-yAxis-text', {
        'dbl-charts-top-yAxis-text--hidden': !hasYAxisName,
      })}
    >
      <div className='dbh-text-exceeds-bubble-left'>
        <TextBubbles
          text={leftYAxisName}
        />
      </div>
      <div className='dbh-text-exceeds-bubble-right'>
        <TextBubbles
          text={rightYAxisName}
        />
      </div>
    </div>
    <div className='analysis-chart-content'>
      <Chart ref={chartRef} option={newOption} notMerge={true} onEvents={onEvents} />
    </div>
  </div>;
};

const LineChart = React.forwardRef(LineChartInner);

export default LineChart;
